import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiPlus, FiEdit2, FiSave, FiX } from 'react-icons/fi';

const emptyForm = { code: '', name: '', description: '', billingInterval: 'monthly', price: '', trialPeriodDays: '14' };

const PlatformSubscriptionPlans = () => {
  const { success, error: showError } = useToast();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const loadAll = () => {
    setLoading(true);
    apiClient.get('/subscriptions/plans')
      .then(({ data }) => setPlans(data.data))
      .catch((err) => showError(getErrorMessage(err, 'Could not load plans')))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/subscriptions/plans', form);
      success('✅ Plan created');
      setShowForm(false);
      setForm(emptyForm);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not create plan')); }
  };

  const startEdit = (plan) => {
    setEditingId(plan.id);
    setEditForm({
      name: plan.name,
      description: plan.description || '',
      price: plan.price,
      billingInterval: plan.billingInterval,
      trialPeriodDays: plan.trialPeriodDays ?? 0,
      isActive: plan.isActive,
    });
  };

  const handleSaveEdit = async (e, planId) => {
    e.preventDefault();
    try {
      await apiClient.patch(`/subscriptions/plans/${planId}`, editForm);
      success('✅ Plan updated');
      setEditingId(null);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not update plan')); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Subscription Plans</h1>
          <p style={{ color: '#6b7280', margin: '0.25rem 0 0 0' }}>Pricing shown to every prospective customer</p>
        </div>
        <button className="btn btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
          <FiPlus size={16} /> {showForm ? 'Cancel' : 'New Plan'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="form-grid" style={{ background: 'white', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <div className="form-field"><label>Code *</label><input required value={form.code} onChange={(e) => setForm(p => ({ ...p, code: e.target.value }))} placeholder="pro" /></div>
          <div className="form-field"><label>Name *</label><input required value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Professional" /></div>
          <div className="form-field"><label>Price *</label><input required type="number" step="0.01" value={form.price} onChange={(e) => setForm(p => ({ ...p, price: e.target.value }))} /></div>
          <div className="form-field">
            <label>Billing Interval</label>
            <select value={form.billingInterval} onChange={(e) => setForm(p => ({ ...p, billingInterval: e.target.value }))}>
              <option value="monthly">Monthly</option>
              <option value="annually">Annually</option>
            </select>
          </div>
          <div className="form-field"><label>Trial Period (days)</label><input type="number" value={form.trialPeriodDays} onChange={(e) => setForm(p => ({ ...p, trialPeriodDays: e.target.value }))} /></div>
          <div className="form-field full-width"><label>Description</label><input value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} /></div>
          <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Create</button></div>
        </form>
      )}

      <div className="table-container" style={{ background: 'white', borderRadius: '8px', overflow: 'hidden' }}>
        <table className="data-table">
          <thead><tr><th>Code</th><th>Name</th><th>Price</th><th>Interval</th><th>Trial Days</th><th>Active</th><th>Actions</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
            ) : plans.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No plans yet</td></tr>
            ) : plans.map((plan) => (
              <tr key={plan.id}>
                {editingId === plan.id ? (
                  <td colSpan={7}>
                    <form onSubmit={(e) => handleSaveEdit(e, plan.id)} className="form-grid" style={{ padding: '1rem 0' }}>
                      <div className="form-field"><label>Name</label><input required value={editForm.name} onChange={(e) => setEditForm(p => ({ ...p, name: e.target.value }))} /></div>
                      <div className="form-field"><label>Price</label><input required type="number" step="0.01" value={editForm.price} onChange={(e) => setEditForm(p => ({ ...p, price: e.target.value }))} /></div>
                      <div className="form-field">
                        <label>Billing Interval</label>
                        <select value={editForm.billingInterval} onChange={(e) => setEditForm(p => ({ ...p, billingInterval: e.target.value }))}>
                          <option value="monthly">Monthly</option>
                          <option value="annually">Annually</option>
                        </select>
                      </div>
                      <div className="form-field"><label>Trial Days</label><input type="number" value={editForm.trialPeriodDays} onChange={(e) => setEditForm(p => ({ ...p, trialPeriodDays: Number(e.target.value) }))} /></div>
                      <div className="form-field">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textTransform: 'none' }}>
                          <input type="checkbox" checked={editForm.isActive} onChange={(e) => setEditForm(p => ({ ...p, isActive: e.target.checked }))} style={{ width: 'auto' }} />
                          Active
                        </label>
                      </div>
                      <div className="form-field full-width form-actions">
                        <button type="submit" className="btn btn-primary" style={{ width: 'auto' }}><FiSave size={14} /> Save</button>
                        <button type="button" className="btn btn-outline" style={{ width: 'auto' }} onClick={() => setEditingId(null)}><FiX size={14} /> Cancel</button>
                      </div>
                    </form>
                  </td>
                ) : (
                  <>
                    <td style={{ fontFamily: 'monospace' }}>{plan.code}</td>
                    <td style={{ fontWeight: 600 }}>{plan.name}</td>
                    <td>£{Number(plan.price).toFixed(2)}</td>
                    <td style={{ textTransform: 'capitalize' }}>{plan.billingInterval}</td>
                    <td>{plan.trialPeriodDays ?? 0}</td>
                    <td><span className="badge" style={{ background: plan.isActive ? '#10b981' : '#9ca3af', color: 'white' }}>{plan.isActive ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      <button className="action-icon edit" title="Edit" onClick={() => startEdit(plan)}>
                        <FiEdit2 size={16} />
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};

export default PlatformSubscriptionPlans;